import { create } from 'zustand'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import {
  db,
  generateId,
  nowISO,
  todayDate,
  queueSync,
  type LocalCycle,
  type LocalCycleCompound,
  type LocalInjectionLog,
} from '@/lib/db'
import { COMPOUND_SEED_DATA, type CompoundSeedData } from '@/data/compounds'

// Cycle compound for the active cycle
export interface ActiveCycleCompound {
  id: string
  compound: CompoundSeedData
  doseMg: number
  frequency: string
  startWeek: number
  endWeek: number
  notes?: string
}

// Injection log entry
export interface InjectionEntry {
  id: string
  date: string
  compoundName: string
  doseMg: number
  site?: string
  oilVolumeMl?: number
  notes?: string
}

interface CycleState {
  // Cycles
  cycles: LocalCycle[]
  activeCycle: LocalCycle | null
  cycleCompounds: ActiveCycleCompound[]
  injections: InjectionEntry[]
  loading: boolean
  error: string | null

  // Actions - Cycle management
  loadCycles: (userId: string) => Promise<void>
  createCycle: (
    userId: string,
    name: string,
    cycleType: 'blast' | 'cruise' | 'pct',
    startDate: string
  ) => Promise<string>
  updateCycle: (cycleId: string, updates: Partial<LocalCycle>) => Promise<void>
  deleteCycle: (cycleId: string) => Promise<void>
  setActiveCycle: (cycleId: string | null) => Promise<void>

  // Actions - Compounds
  addCompoundToCycle: (
    cycleId: string,
    compoundName: string,
    doseMg: number,
    frequency: string,
    startWeek: number,
    endWeek: number,
    notes?: string
  ) => Promise<void>
  removeCompoundFromCycle: (compoundId: string) => Promise<void>
  updateCycleCompound: (compoundId: string, updates: Partial<ActiveCycleCompound>) => Promise<void>

  // Actions - Injections
  loadInjections: (userId: string, cycleId?: string) => Promise<void>
  logInjection: (
    userId: string,
    compoundName: string,
    doseMg: number,
    site?: string,
    oilVolumeMl?: number,
    notes?: string,
    cycleId?: string
  ) => Promise<void>
  deleteInjection: (injectionId: string) => Promise<void>

  // Helpers
  loadCycleCompounds: (cycleId: string) => Promise<void>
  getCompoundByName: (name: string) => CompoundSeedData | undefined
  getRecentInjections: (limit?: number) => InjectionEntry[]
  getInjectionsByCompound: (compoundName: string) => InjectionEntry[]
}

export const useCycleStore = create<CycleState>()((set, get) => ({
  cycles: [],
  activeCycle: null,
  cycleCompounds: [],
  injections: [],
  loading: false,
  error: null,

  loadCycles: async (userId: string) => {
    set({ loading: true, error: null })

    try {
      // Always load from IndexedDB first (offline-first approach)
      const localCycles = await db.cycles
        .where('user_id')
        .equals(userId)
        .toArray()

      // Sort by created_at descending
      localCycles.sort((a, b) => b.created_at.localeCompare(a.created_at))

      // Find active cycle from local data
      let activeCycle = localCycles.find((c) => c.status === 'active') || null

      // Set initial state from local data immediately
      set({ cycles: localCycles, activeCycle, loading: false })

      // Load compounds for active cycle
      if (activeCycle) {
        await get().loadCycleCompounds(activeCycle.id)
      }

      // Then sync with Supabase in background if configured
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('cycles')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })

          if (!error && data && data.length > 0) {
            const remoteCycles: LocalCycle[] = data.map((row) => ({
              id: row.id,
              user_id: row.user_id,
              name: row.name,
              start_date: row.start_date,
              end_date: row.end_date ?? undefined,
              status: row.status as 'planning' | 'active' | 'completed',
              cycle_type: row.cycle_type as 'blast' | 'cruise' | 'pct',
              notes: row.notes ?? undefined,
              sync_status: 'synced' as const,
              created_at: row.created_at,
              updated_at: row.updated_at,
            }))

            // Merge: keep local pending items not on remote
            const pendingLocal = localCycles.filter((c) => c.sync_status === 'pending')
            const remoteIds = new Set(remoteCycles.map((c) => c.id))
            const pendingNotOnRemote = pendingLocal.filter((c) => !remoteIds.has(c.id))

            // Combine remote cycles with any pending local-only cycles
            const mergedCycles = [...remoteCycles, ...pendingNotOnRemote]
              .sort((a, b) => b.created_at.localeCompare(a.created_at))

            // Cache remote cycles in IndexedDB
            await db.cycles.bulkPut(remoteCycles)

            // Update state with merged data
            activeCycle = mergedCycles.find((c) => c.status === 'active') || null
            set({ cycles: mergedCycles, activeCycle })

            // Reload compounds if active cycle changed
            if (activeCycle) {
              await get().loadCycleCompounds(activeCycle.id)
            }
          }
        } catch (syncError) {
          // Supabase sync failed, but we already have local data - that's fine
          console.warn('Supabase sync failed, using local data:', syncError)
        }
      }
    } catch (error) {
      console.error('Load cycles error:', error)
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load cycles',
      })
    }
  },

  loadCycleCompounds: async (cycleId: string) => {
    try {
      // Load from IndexedDB first
      let localCompounds = await db.cycleCompounds
        .where('cycle_id')
        .equals(cycleId)
        .toArray()

      // Set initial state from local data
      const toActiveCompounds = (compounds: LocalCycleCompound[]): ActiveCycleCompound[] =>
        compounds.map((c) => ({
          id: c.id,
          compound: COMPOUND_SEED_DATA.find((comp) => comp.name === c.compound_id) || COMPOUND_SEED_DATA[0],
          doseMg: c.dose_mg,
          frequency: c.frequency,
          startWeek: c.start_week,
          endWeek: c.end_week,
          notes: c.notes,
        }))

      set({ cycleCompounds: toActiveCompounds(localCompounds) })

      // Sync from Supabase if configured
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('cycle_compounds')
            .select('*')
            .eq('cycle_id', cycleId)

          if (!error && data && data.length > 0) {
            const remoteCompounds: LocalCycleCompound[] = data.map((row) => ({
              id: row.id,
              cycle_id: row.cycle_id,
              compound_id: row.compound_id,
              dose_mg: row.dose_mg,
              frequency: row.frequency,
              start_week: row.start_week,
              end_week: row.end_week,
              notes: row.notes ?? undefined,
              sync_status: 'synced' as const,
              created_at: row.created_at,
            }))

            // Cache in IndexedDB
            await db.cycleCompounds.bulkPut(remoteCompounds)

            // Merge: keep local pending items not on remote
            const pendingLocal = localCompounds.filter((c) => c.sync_status === 'pending')
            const remoteIds = new Set(remoteCompounds.map((c) => c.id))
            const pendingNotOnRemote = pendingLocal.filter((c) => !remoteIds.has(c.id))

            const mergedCompounds = [...remoteCompounds, ...pendingNotOnRemote]

            set({ cycleCompounds: toActiveCompounds(mergedCompounds) })
          }
        } catch (syncError) {
          console.warn('Supabase compound sync failed, using local data:', syncError)
        }
      }
    } catch (error) {
      console.error('Load cycle compounds error:', error)
    }
  },

  createCycle: async (userId, name, cycleType, startDate) => {
    const now = nowISO()
    const cycleId = generateId()

    const newCycle: LocalCycle = {
      id: cycleId,
      user_id: userId,
      name,
      start_date: startDate,
      status: 'planning',
      cycle_type: cycleType,
      sync_status: 'pending',
      created_at: now,
      updated_at: now,
    }

    try {
      await db.cycles.add(newCycle)

      set((state) => ({
        cycles: [newCycle, ...state.cycles],
      }))

      if (isSupabaseConfigured()) {
        const { error } = await supabase.from('cycles').insert({
          id: cycleId,
          user_id: userId,
          name,
          start_date: startDate,
          status: 'planning',
          cycle_type: cycleType,
        })

        if (error) {
          await queueSync('cycles', cycleId, 'insert', newCycle as unknown as Record<string, unknown>)
        } else {
          await db.cycles.update(cycleId, { sync_status: 'synced' })
        }
      }

      return cycleId
    } catch (error) {
      console.error('Create cycle error:', error)
      throw error
    }
  },

  updateCycle: async (cycleId, updates) => {
    const now = nowISO()

    set((state) => ({
      cycles: state.cycles.map((c) =>
        c.id === cycleId ? { ...c, ...updates, updated_at: now } : c
      ),
      activeCycle:
        state.activeCycle?.id === cycleId
          ? { ...state.activeCycle, ...updates, updated_at: now }
          : state.activeCycle,
    }))

    try {
      await db.cycles.update(cycleId, { ...updates, updated_at: now, sync_status: 'pending' })

      if (isSupabaseConfigured()) {
        const { error } = await supabase
          .from('cycles')
          .update(updates)
          .eq('id', cycleId)

        if (error) {
          await queueSync('cycles', cycleId, 'update', updates as Record<string, unknown>)
        } else {
          await db.cycles.update(cycleId, { sync_status: 'synced' })
        }
      }
    } catch (error) {
      console.error('Update cycle error:', error)
    }
  },

  deleteCycle: async (cycleId) => {
    set((state) => ({
      cycles: state.cycles.filter((c) => c.id !== cycleId),
      activeCycle: state.activeCycle?.id === cycleId ? null : state.activeCycle,
      cycleCompounds: state.activeCycle?.id === cycleId ? [] : state.cycleCompounds,
    }))

    try {
      await db.cycles.delete(cycleId)
      await db.cycleCompounds.where('cycle_id').equals(cycleId).delete()

      if (isSupabaseConfigured()) {
        await supabase.from('cycle_compounds').delete().eq('cycle_id', cycleId)
        await supabase.from('cycles').delete().eq('id', cycleId)
      }
    } catch (error) {
      console.error('Delete cycle error:', error)
    }
  },

  setActiveCycle: async (cycleId) => {
    const { cycles, activeCycle: currentActive } = get()

    // Deactivate the currently active cycle if there is one and it's different
    if (currentActive && currentActive.id !== cycleId) {
      await get().updateCycle(currentActive.id, { status: 'completed' })
    }

    const newActiveCycle = cycleId ? cycles.find((c) => c.id === cycleId) || null : null
    set({ activeCycle: newActiveCycle })

    // Activate the new cycle
    if (newActiveCycle) {
      await get().updateCycle(newActiveCycle.id, { status: 'active' })
      get().loadCycleCompounds(newActiveCycle.id)
    } else {
      set({ cycleCompounds: [] })
    }
  },

  addCompoundToCycle: async (cycleId, compoundName, doseMg, frequency, startWeek, endWeek, notes) => {
    const now = nowISO()
    const compoundId = generateId()

    const compound = COMPOUND_SEED_DATA.find((c) => c.name === compoundName)
    if (!compound) {
      throw new Error(`Compound not found: ${compoundName}`)
    }

    const newCompound: LocalCycleCompound = {
      id: compoundId,
      cycle_id: cycleId,
      compound_id: compoundName,
      dose_mg: doseMg,
      frequency,
      start_week: startWeek,
      end_week: endWeek,
      notes,
      sync_status: 'pending',
      created_at: now,
    }

    const activeCompound: ActiveCycleCompound = {
      id: compoundId,
      compound,
      doseMg,
      frequency,
      startWeek,
      endWeek,
      notes,
    }

    set((state) => ({
      cycleCompounds: [...state.cycleCompounds, activeCompound],
    }))

    try {
      await db.cycleCompounds.add(newCompound)

      if (isSupabaseConfigured()) {
        const { error } = await supabase.from('cycle_compounds').insert({
          id: compoundId,
          cycle_id: cycleId,
          compound_id: compoundName,
          dose_mg: doseMg,
          frequency,
          start_week: startWeek,
          end_week: endWeek,
          notes: notes ?? null,
        })

        if (error) {
          await queueSync('cycle_compounds', compoundId, 'insert', newCompound as unknown as Record<string, unknown>)
        } else {
          await db.cycleCompounds.update(compoundId, { sync_status: 'synced' })
        }
      }
    } catch (error) {
      console.error('Add compound error:', error)
    }
  },

  removeCompoundFromCycle: async (compoundId) => {
    set((state) => ({
      cycleCompounds: state.cycleCompounds.filter((c) => c.id !== compoundId),
    }))

    try {
      await db.cycleCompounds.delete(compoundId)

      if (isSupabaseConfigured()) {
        await supabase.from('cycle_compounds').delete().eq('id', compoundId)
      }
    } catch (error) {
      console.error('Remove compound error:', error)
    }
  },

  updateCycleCompound: async (compoundId, updates) => {
    set((state) => ({
      cycleCompounds: state.cycleCompounds.map((c) =>
        c.id === compoundId ? { ...c, ...updates } : c
      ),
    }))

    const dbUpdates: Partial<LocalCycleCompound> = {}
    if (updates.doseMg !== undefined) dbUpdates.dose_mg = updates.doseMg
    if (updates.frequency !== undefined) dbUpdates.frequency = updates.frequency
    if (updates.startWeek !== undefined) dbUpdates.start_week = updates.startWeek
    if (updates.endWeek !== undefined) dbUpdates.end_week = updates.endWeek
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes

    try {
      await db.cycleCompounds.update(compoundId, { ...dbUpdates, sync_status: 'pending' })

      if (isSupabaseConfigured()) {
        await supabase.from('cycle_compounds').update(dbUpdates).eq('id', compoundId)
      }
    } catch (error) {
      console.error('Update compound error:', error)
    }
  },

  loadInjections: async (userId, cycleId) => {
    try {
      let query = db.injectionLogs.where('user_id').equals(userId)

      const logs = cycleId
        ? await query.and((log) => log.cycle_id === cycleId).reverse().sortBy('date')
        : await query.reverse().sortBy('date')

      const injections: InjectionEntry[] = logs.map((log) => ({
        id: log.id,
        date: log.date,
        compoundName: log.compound_id,
        doseMg: log.dose_mg,
        site: log.injection_site,
        oilVolumeMl: log.oil_volume_ml,
        notes: log.notes,
      }))

      set({ injections })
    } catch (error) {
      console.error('Load injections error:', error)
    }
  },

  logInjection: async (userId, compoundName, doseMg, site, oilVolumeMl, notes, cycleId) => {
    const now = nowISO()
    const today = todayDate()
    const injectionId = generateId()

    const newLog: LocalInjectionLog = {
      id: injectionId,
      user_id: userId,
      cycle_id: cycleId,
      date: today,
      compound_id: compoundName,
      dose_mg: doseMg,
      injection_site: site,
      oil_volume_ml: oilVolumeMl,
      notes,
      sync_status: 'pending',
      created_at: now,
    }

    const entry: InjectionEntry = {
      id: injectionId,
      date: today,
      compoundName,
      doseMg,
      site,
      oilVolumeMl,
      notes,
    }

    set((state) => ({
      injections: [entry, ...state.injections],
    }))

    try {
      await db.injectionLogs.add(newLog)

      if (isSupabaseConfigured()) {
        const { error } = await supabase.from('injection_logs').insert({
          id: injectionId,
          user_id: userId,
          cycle_id: cycleId ?? null,
          date: today,
          compound_id: compoundName,
          dose_mg: doseMg,
          injection_site: site ?? null,
          oil_volume_ml: oilVolumeMl ?? null,
          notes: notes ?? null,
        })

        if (error) {
          await queueSync('injection_logs', injectionId, 'insert', newLog as unknown as Record<string, unknown>)
        } else {
          await db.injectionLogs.update(injectionId, { sync_status: 'synced' })
        }
      }
    } catch (error) {
      console.error('Log injection error:', error)
    }
  },

  deleteInjection: async (injectionId) => {
    set((state) => ({
      injections: state.injections.filter((i) => i.id !== injectionId),
    }))

    try {
      await db.injectionLogs.delete(injectionId)

      if (isSupabaseConfigured()) {
        await supabase.from('injection_logs').delete().eq('id', injectionId)
      }
    } catch (error) {
      console.error('Delete injection error:', error)
    }
  },

  getCompoundByName: (name) => {
    return COMPOUND_SEED_DATA.find((c) => c.name === name || c.shortName === name)
  },

  getRecentInjections: (limit = 10) => {
    return get().injections.slice(0, limit)
  },

  getInjectionsByCompound: (compoundName) => {
    return get().injections.filter((i) => i.compoundName === compoundName)
  },
}))
